import { makeAutoObservable } from "mobx";

import { IChatExchangeError } from "../models/interfaces/chat-exchange-error.interface";
import { ChatThreadExtendedModel } from "../models/interfaces/chat-thread-pending-model.interface";

export class ThreadStore {
  private _threads: ChatThreadExtendedModel[] = [];
  private _activeThread: ChatThreadExtendedModel | undefined;
  private _threadErrors: IChatExchangeError[] = [];

  public loadingThreads: boolean = false;

  public pendingCopyResponseToInput?: string;

  constructor() {
    makeAutoObservable(this);
  }

  public get threads(): ChatThreadExtendedModel[] {
    return this._threads;
  }

  public set threads(threads: ChatThreadExtendedModel[]) {
    this._threads = threads;
  }

  public get activeThread(): ChatThreadExtendedModel | undefined {
    return this._activeThread;
  }

  public get threadErrors(): IChatExchangeError[] {
    return this._threadErrors;
  }

  public set threadErrors(errors: IChatExchangeError[]) {
    this._threadErrors = errors;
  }

  public get hasThreadErrors(): boolean {
    return this._threadErrors.length > 0;
  }

  public triggerCopyResponseToInput(input: string | undefined): void {
    this.pendingCopyResponseToInput = input;
  }

  public setActiveThread(thread: ChatThreadExtendedModel): void {
    this._activeThread = thread;
  }

  public resetActiveThread(): void {
    this._activeThread = undefined;
  }

  // NOTE #63781 Called after the BE response is received to persist the final thread state.
  // Updates the activeThread if it matches, then either updates the existing thread or,
  // for a new thread that does not exist yet, adds it in the _threads list.
  // A missing thread is expected here (new thread), so it is not treated as an error.
  public updateOrCreatePendingThread(updatedPendingThread: ChatThreadExtendedModel): void {
    if (this._activeThread?.id === updatedPendingThread.id) {
      this.setActiveThread(updatedPendingThread);
    }

    const index = this._threads.findIndex(t => t.id === updatedPendingThread.id);
    if (index !== -1) {
      this._threads[index] = updatedPendingThread;
    } else if (updatedPendingThread.id) {
      this._threads.push(updatedPendingThread);
    }
  }

  // NOTE #63781 Called before the BE response to reflect a submitted prompt in a pending thread.
  // Sets the activeThread (when it matches threadId or when the request comes from a new thread) and
  // updates pendingPrompt on the existing thread so the pending state shows immediately.
  // The final state is written later by updateOrCreatePendingThread once the BE responds.
  public setPendingPromptInThread(threadId: string | undefined, pendingPrompt: string | undefined): void {
    if (this._activeThread?.id === threadId) {
      this.setActiveThread({ ...this._activeThread, id: threadId, pendingPrompt } as ChatThreadExtendedModel);
    }

    const index = this._threads.findIndex(t => t.id === threadId);
    if (index !== -1) {
      this._threads[index].pendingPrompt = pendingPrompt;
    }
  }
}
